import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import WebPageLayout from '../../../components/layout/WebPageLayout';
import DSLoading from '../../../components/designSystem/DSLoading';
import mainLogo from '../../../assets/Logo/main_logo.png';
import backgroundImage from '../../../assets/img/background.png';
import eyeOffIcon from '../../../assets/Icon/project/JoinMembershiIcons.png';
import { PATHS } from '../../../routes/paths';
import { usePasswordReset } from '../usePasswordReset';
import { convertToVwWeb } from '../../../Utils/responsive';

const MAIN_TEXT = '#17171b';
const BTN_TEAL = '#026478';
const BTN_DISABLED_BG = '#e0e0e0';
const BTN_DISABLED_TEXT = '#9e9e9e';

type Props = { token: string };

const PasswordResetWeb = ({ token }: Props) => {
  const { i18n, t } = useTranslation('translation');
  const navigate = useNavigate();
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [newPwFocused, setNewPwFocused] = React.useState(false);
  const [confirmPwFocused, setConfirmPwFocused] = React.useState(false);
  const {
    phase,
    record,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    passwordMismatchError,
    isSubmitting,
    isSubmitDisabled,
    handleSubmit,
  } = usePasswordReset(token);

  const goLogin = () => {
    navigate(PATHS.LOGIN);
  };

  const goToMain = () => {
    navigate(PATHS.DASHBOARD);
  };

  const renderBody = () => {
    if (phase === 'loading') {
      return (
        <LoadingWrap>
          <DSLoading variant="content" size={220} marginTop="24px" />
        </LoadingWrap>
      );
    }
    if (phase === 'invalid') {
      return (
        <ContentColumn>
          <Title>{t('page.passwordReset.invalidTitle')}</Title>
          <InvalidBody>
            {t('page.passwordReset.invalidDescriptionLine1')}
            <br />
            {t('page.passwordReset.invalidDescriptionLine2')}
          </InvalidBody>
          <PrimaryButton type="button" onClick={goLogin}>
            {t('page.passwordReset.goLogin')}
          </PrimaryButton>
        </ContentColumn>
      );
    }
    if (phase === 'success') {
      return null;
    }
    const hasMismatch = Boolean(passwordMismatchError);
    const ph = t('page.passwordReset.passwordInputPlaceholder');
    const showFloatNew = newPwFocused || Boolean(password);
    const showFloatConfirm = confirmPwFocused || Boolean(confirmPassword);
    return (
      <ContentColumn>
        <Title>{t('page.passwordReset.title')}</Title>
        <InstructionText>{t('page.passwordReset.descriptionLine1')}</InstructionText>
        {record?.user_email ? <EmailText>{record.user_email}</EmailText> : null}
        <Form onSubmit={handleSubmit}>
          <MaLabeledFieldBlock>
            <MaFieldCaption htmlFor="pw-reset-new">{t('page.passwordReset.passwordLabel')}</MaFieldCaption>
            <MaInputBox $error={hasMismatch} $active={showFloatNew}>
              {showFloatNew ? <MaInnerLabel htmlFor="pw-reset-new">{ph}</MaInnerLabel> : null}
              <MaInputRow>
                <MaFieldInput
                  id="pw-reset-new"
                  type={showNewPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setNewPwFocused(true)}
                  onBlur={() => setNewPwFocused(false)}
                  placeholder={showFloatNew ? '' : ph}
                  autoComplete="new-password"
                />
                {password ? (
                  <MaInputActions>
                    <IconButton type="button" onClick={() => setShowNewPassword((v) => !v)} aria-label="toggle password">
                      {showNewPassword ? <EyeOnSvg /> : <EyeOffImg />}
                    </IconButton>
                    <IconButton
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setPassword('')}
                      aria-label="clear password"
                    >
                      <ClearSvg />
                    </IconButton>
                  </MaInputActions>
                ) : null}
              </MaInputRow>
            </MaInputBox>
          </MaLabeledFieldBlock>
          <MaLabeledFieldBlock>
            <MaFieldCaption htmlFor="pw-reset-confirm">{t('page.passwordReset.passwordConfirmLabel')}</MaFieldCaption>
            <MaInputErrorStack>
              <MaInputBox $error={hasMismatch} $active={showFloatConfirm}>
                {showFloatConfirm ? <MaInnerLabel htmlFor="pw-reset-confirm">{ph}</MaInnerLabel> : null}
                <MaInputRow>
                  <MaFieldInput
                    id="pw-reset-confirm"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setConfirmPwFocused(true)}
                    onBlur={() => setConfirmPwFocused(false)}
                    placeholder={showFloatConfirm ? '' : ph}
                    autoComplete="new-password"
                  />
                  {confirmPassword ? (
                    <MaInputActions>
                      <IconButton type="button" onClick={() => setShowConfirmPassword((v) => !v)} aria-label="toggle password">
                        {showConfirmPassword ? <EyeOnSvg /> : <EyeOffImg />}
                      </IconButton>
                      <IconButton
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setConfirmPassword('')}
                        aria-label="clear confirm password"
                      >
                        <ClearSvg />
                      </IconButton>
                    </MaInputActions>
                  ) : null}
                </MaInputRow>
              </MaInputBox>
              {passwordMismatchError ? <MaFieldErrorText>{passwordMismatchError}</MaFieldErrorText> : null}
            </MaInputErrorStack>
          </MaLabeledFieldBlock>
          <SubmitButton type="submit" disabled={isSubmitDisabled}>
            {isSubmitting ? t('page.passwordReset.actions.submitting') : t('page.passwordReset.actions.submit')}
          </SubmitButton>
        </Form>
      </ContentColumn>
    );
  };

  return (
    <WebPageLayout title="" hideHeader hideFooter>
      <BackgroundOverlay />
      <Centered>
        <ResetContainer>
          <HeaderRow>
            <LangSelect value={currentLang} onChange={(e) => i18n.changeLanguage(e.target.value)}>
              <option value="ko">{t('lang.ko')}</option>
              <option value="en">{t('lang.en')}</option>
            </LangSelect>
          </HeaderRow>
          <LogoSection>
            <LogoLink to={PATHS.LOGIN}>
              <Logo src={mainLogo} alt="Mission Fund" />
            </LogoLink>
          </LogoSection>
          {renderBody()}
        </ResetContainer>
      </Centered>
      {phase === 'success' ? (
        <SuccessModalOverlay>
          <SuccessModalCard>
            <SuccessModalTitle>{t('page.passwordReset.successTitle')}</SuccessModalTitle>
            <SuccessModalDescription>
              {t('page.passwordReset.successBodyLine1')}
              <br />
              {t('page.passwordReset.successBodyLine2Web')}
            </SuccessModalDescription>
            <SuccessModalButton type="button" onClick={goToMain}>
              {t('page.passwordReset.successButtonWeb')}
            </SuccessModalButton>
          </SuccessModalCard>
        </SuccessModalOverlay>
      ) : null}
    </WebPageLayout>
  );
};

const EyeOnSvg = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5ZM12 17.5C9.24 17.5 7 15.26 7 12.5C7 9.74 9.24 7.5 12 7.5C14.76 7.5 17 9.74 17 12.5C17 15.26 14.76 17.5 12 17.5ZM12 9.5C10.34 9.5 9 10.84 9 12.5C9 14.16 10.34 15.5 12 15.5C13.66 15.5 15 14.16 15 12.5C15 10.84 13.66 9.5 12 9.5Z"
      fill="#676767"
    />
  </svg>
);

const EyeOffImg = () => <img src={eyeOffIcon} alt="" width="24" height="24" />;

const ClearSvg = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#BCBCBC" />
    <path d="M8 8L16 16M16 8L8 16" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const BackgroundOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
  pointer-events: none;
`;

/** 로그인 페이지(`Login/web`)와 동일 */
const Centered = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 0;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const ResetContainer = styled.div`
  width: 560px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const HeaderRow = styled.div`
  position: absolute;
  top: -55px;
  right: -179px;
  z-index: 10;
`;

const LangSelect = styled.select`
  width: auto;
  min-width: 118px;
  height: 42px;
  padding: 8px 36px 8px 12px;
  border-radius: 4px;
  border: 1px solid #bcbcbc;
  background: white;
  color: #676767;
  font-family: 'Pretendard', sans-serif;
  font-size: 20px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='8' viewBox='0 0 14 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L7 7L13 1' stroke='%23BCBCBC' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 14px 8px;
`;

const LogoSection = styled.div`
  width: 272px;
  height: 85px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  /* 로고 ↔ 제목(비밀번호 변경) 44px */
  margin-bottom: 44px;
`;

const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`;

const Logo = styled.img`
  width: 252px;
  height: 65px;
  object-fit: contain;
`;

const ContentColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 0 16px;
  /* 제목 ↔ 안내 문구 16px */
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 32px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
`;

const InstructionText = styled.p`
  margin: 0;
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  max-width: 360px;
`;

const InvalidBody = styled.p`
  margin: 12px 0 0;
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 150%;
  max-width: 360px;
`;

const EmailText = styled.p`
  margin: 36px 0 0;
  /* 안내 문구 ↔ 이메일 36px */
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  word-break: break-all;
`;

const Form = styled.form`
  width: 100%;
  /* 이메일 ↔ 첫 입력 필드(라벨+인풋 블록 시작) 20px — 이메일 없을 때는 안내↔폼 20px */
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

/** 캡션–입력 8px (에러는 InputGroup 과 같이 박스 아래 4px 만) */
const MaLabeledFieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const MaFieldCaption = styled.label`
  width: ${convertToVwWeb(353)};
  margin-bottom: 8px;
  color: ${MAIN_TEXT};
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 140%;
  text-align: left;
`;

/** 나의 계정 InputGroup: 박스–에러 gap 4px */
const MaInputErrorStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwWeb(4)};
  width: 100%;
`;

/** 나의 계정 InputBox 와 동일 (테두리·반경·패딩) + 지정 폭 353px */
const MaInputBox = styled.div<{ $error?: boolean; $active?: boolean }>`
  box-sizing: border-box;
  background-color: #ffffff;
  border: ${({ $error, $active, theme }) =>
    $error ? '1.6px solid #ff0000' : $active ? `1.6px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`};
  border-radius: ${convertToVwWeb(4)};
  padding: ${convertToVwWeb(8)} ${convertToVwWeb(12)};
  width: ${convertToVwWeb(353)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(2)};
  position: relative;
  justify-content: ${({ $active }) => ($active ? 'flex-start' : 'center')};
  min-height: ${({ $active }) => ($active ? convertToVwWeb(74) : convertToVwWeb(58))};
  /* ContentColumn text-align: center 상속 방지 (나의 계정 입력과 동일하게 좌측) */
  text-align: left;
`;

const MaInnerLabel = styled.label`
  ${({ theme }) => theme.typography.contents.regular16};
  color: ${({ theme }) => theme.colors.grayScale[60]};
  text-align: left;
  width: 100%;
`;

/** 입력 + 아이콘: flex, align-items center, gap 16px */
const MaInputRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${convertToVwWeb(16)};
`;

const MaFieldInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  text-align: left;
  ${({ theme }) => theme.typography.contents.regular24};
  color: ${({ theme }) => theme.colors.grayScale[90]};

  &::placeholder {
    color: ${({ theme }) => theme.colors.grayScale[50]};
  }
`;

const MaInputActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
`;

/** 나의 계정 ErrorText 와 동일 */
const MaFieldErrorText = styled.p`
  ${({ theme }) => theme.typography.contents.regular20};
  color: #ff0000;
  margin: 0;
  width: ${convertToVwWeb(353)};
  align-self: center;
  text-align: left;
`;

const SubmitButton = styled.button`
  width: ${convertToVwWeb(353)};
  height: 52px;
  margin-top: 8px;
  border: none;
  border-radius: 12px;
  background: ${({ disabled }) => (disabled ? BTN_DISABLED_BG : 'var(--PrimaryBlue-80, #026478)')};
  color: ${({ disabled }) => (disabled ? BTN_DISABLED_TEXT : '#ffffff')};
  font-family: Pretendard, sans-serif;
  font-size: 17px;
  font-weight: 600;
  line-height: 150%;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const PrimaryButton = styled.button`
  width: 100%;
  max-width: 360px;
  height: 52px;
  margin-top: 28px;
  border: none;
  border-radius: 12px;
  background: var(--PrimaryBlue-80, #026478);
  color: #ffffff;
  font-family: Pretendard, sans-serif;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
`;

const LoadingWrap = styled.div`
  width: 100%;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SuccessModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(23, 23, 27, 0.42);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const SuccessModalCard = styled.div`
  display: flex;
  width: min(480px, calc(100vw - 40px));
  padding: 48px 32px 32px;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.35);
`;

const SuccessModalTitle = styled.h2`
  margin: 0;
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 26px;
  font-weight: 700;
  line-height: 130%;
`;

const SuccessModalDescription = styled.p`
  margin: 0;
  color: ${MAIN_TEXT};
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 150%;
  max-width: 340px;
`;

const SuccessModalButton = styled.button`
  width: 100%;
  max-width: 320px;
  height: 56px;
  margin-top: 8px;
  border: none;
  border-radius: 12px;
  background: ${BTN_TEAL};
  color: #fff;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 150%;
  cursor: pointer;
`;

export default PasswordResetWeb;
